import { Capacitor } from '@capacitor/core';

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export interface PWAInstallPrompt {
  event: BeforeInstallPromptEvent;
  showPrompt(): Promise<void>;
}

class PWAService {
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private isInstalled = false;
  private isStandalone = false;

  constructor() {
    this.checkInstallationStatus();
    this.setupEventListeners();
  }

  private checkInstallationStatus() {
    // Check if app is installed (standalone mode)
    this.isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    // Check if app is already installed
    this.isInstalled =
      this.isStandalone || localStorage.getItem('yapzap-installed') === 'true';
  }

  private setupEventListeners() {
    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e as BeforeInstallPromptEvent;
      console.log('[PWA] Install prompt captured');

      // Dispatch custom event for components to listen to
      window.dispatchEvent(new CustomEvent('pwa-install-available'));
    });

    // Listen for appinstalled event
    window.addEventListener('appinstalled', () => {
      this.isInstalled = true;
      this.deferredPrompt = null;
      localStorage.setItem('yapzap-installed', 'true');
      console.log('[PWA] App installed successfully');

      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('pwa-installed'));
    });

    // Listen for display mode changes
    window
      .matchMedia('(display-mode: standalone)')
      .addEventListener('change', (e) => {
        this.isStandalone = e.matches;
        this.isInstalled = this.isStandalone;
        console.log('[PWA] Display mode changed:', this.isStandalone);
      });
  }

  async registerServiceWorker(): Promise<boolean> {
    if (Capacitor.isNativePlatform()) {
      console.log('[PWA] Native platform detected - unregistering any web service workers');
      await this.unregisterServiceWorker();
      return false;
    }

    // Do not register a service worker during local development.
    // It interferes with Vite HMR and API proxy when using self-signed HTTPS.
    if (import.meta.env.DEV) {
      console.warn('[PWA] Service Worker disabled in development');
      // Also unregister any existing service workers for clean dev environment
      await this.unregisterServiceWorker();
      return false;
    }
    if (!('serviceWorker' in navigator)) {
      console.warn(
        '[PWA] Service Worker not supported - navigator.serviceWorker is undefined'
      );
      return false;
    }

    // Check if we're on HTTPS or localhost
    const isSecure =
      window.location.protocol === 'https:' ||
      window.location.hostname === 'localhost';
    if (!isSecure) {
      console.warn(
        '[PWA] Service Worker requires HTTPS in production. Current protocol:',
        window.location.protocol
      );
      console.warn(
        '[PWA] Service Worker will not register on HTTP in production'
      );
      return false;
    }

    try {
      console.log('[PWA] Attempting to register service worker...');
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      console.log(
        '[PWA] Service Worker registered successfully:',
        registration
      );

      // Handle service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            console.log('[PWA] Service worker state changed:', newWorker.state);
            if (
              newWorker.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              console.log('[PWA] New service worker available');
              window.dispatchEvent(new CustomEvent('pwa-update-available'));
            }
          });
        }
      });

      // Handle service worker controller change
      // Only reload if a previous controller existed (meaning this is an update, not initial load)
      let refreshing = false;
      const hadController = !!navigator.serviceWorker.controller;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('[PWA] Service worker controller changed');
        // Prevent infinite reload loop:
        // - Only reload if there was a previous controller (not first-time install)
        // - Only reload once per session
        if (hadController && !refreshing) {
          refreshing = true;
          console.log('[PWA] Reloading page due to service worker update');
          window.location.reload();
        } else {
          console.log('[PWA] Skipping reload - first controller activation or already refreshing');
        }
      });

      return true;
    } catch (error) {
      console.error('[PWA] Service Worker registration failed:', error);
      if (error instanceof Error) {
        console.error('[PWA] Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
        });
      }
      return false;
    }
  }

  async showInstallPrompt(): Promise<boolean> {
    if (!this.deferredPrompt) {
      console.warn('[PWA] No install prompt available');
      return false;
    }

    try {
      await this.deferredPrompt.prompt();
      const choiceResult = await this.deferredPrompt.userChoice;

      if (choiceResult.outcome === 'accepted') {
        console.log('[PWA] User accepted install prompt');
        this.deferredPrompt = null;
        return true;
      } else {
        console.log('[PWA] User dismissed install prompt');
        return false;
      }
    } catch (error) {
      console.error('[PWA] Error showing install prompt:', error);
      return false;
    }
  }

  canInstall(): boolean {
    return this.deferredPrompt !== null && !this.isInstalled;
  }

  isAppInstalled(): boolean {
    return this.isInstalled;
  }

  isStandaloneMode(): boolean {
    return this.isStandalone;
  }

  async unregisterServiceWorker(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      return true;
    }

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      // Only unregister the PWA service worker (sw.js), not Firebase messaging service worker
      const unregisterPromises = registrations
        .filter(registration => {
          const swUrl = registration.active?.scriptURL || registration.waiting?.scriptURL || registration.installing?.scriptURL;
          // Keep Firebase messaging service worker for push notifications
          if (swUrl && swUrl.includes('firebase-messaging-sw.js')) {
            console.log('[PWA] Preserving Firebase messaging service worker');
            return false;
          }
          return true;
        })
        .map(registration => registration.unregister());
      await Promise.all(unregisterPromises);
      console.log('[PWA] PWA service workers unregistered successfully');
      return true;
    } catch (error) {
      console.error('[PWA] Error unregistering service workers:', error);
      return false;
    }
  }

  async checkForUpdates(): Promise<void> {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.update();
      }
    }
  }

  async skipWaiting(): Promise<void> {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
  }

  // Request notification permission
  async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('[PWA] Notifications not supported');
      return 'denied';
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      console.log('[PWA] Notification permission:', permission);
      return permission;
    }

    return Notification.permission;
  }

  // Show notification
  async showNotification(
    title: string,
    options?: NotificationOptions
  ): Promise<void> {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      console.warn('[PWA] Cannot show notification - permission not granted');
      return;
    }

    try {
      const notification = new Notification(title, {
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png',
        ...options,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch (error) {
      console.error('[PWA] Error showing notification:', error);
    }
  }

  // Get app version from service worker
  async getAppVersion(): Promise<string | null> {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      return new Promise((resolve) => {
        const messageChannel = new MessageChannel();
        messageChannel.port1.onmessage = (event) => {
          resolve(event.data.version || null);
        };
        const controller = navigator.serviceWorker.controller;
        if (controller) {
          controller.postMessage({ type: 'GET_VERSION' }, [
            messageChannel.port2,
          ]);
        }
      });
    }
    return null;
  }

  // Check if app is running in PWA mode
  isPWAMode(): boolean {
    return (
      this.isStandalone ||
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    );
  }

  // Get install prompt event
  getInstallPrompt(): BeforeInstallPromptEvent | null {
    return this.deferredPrompt;
  }

  // Clear install prompt
  clearInstallPrompt(): void {
    this.deferredPrompt = null;
  }
}

// Create singleton instance
export const pwaService = new PWAService();

// Export utility functions
export const registerServiceWorker = () => pwaService.registerServiceWorker();
export const showInstallPrompt = () => pwaService.showInstallPrompt();
export const canInstall = () => pwaService.canInstall();
export const isAppInstalled = () => pwaService.isAppInstalled();
export const isStandaloneMode = () => pwaService.isStandaloneMode();
export const checkForUpdates = () => pwaService.checkForUpdates();
export const skipWaiting = () => pwaService.skipWaiting();
export const requestNotificationPermission = () =>
  pwaService.requestNotificationPermission();
export const showNotification = (
  title: string,
  options?: NotificationOptions
) => pwaService.showNotification(title, options);
export const getAppVersion = () => pwaService.getAppVersion();
export const isPWAMode = () => pwaService.isPWAMode();
export const getInstallPrompt = () => pwaService.getInstallPrompt();
export const clearInstallPrompt = () => pwaService.clearInstallPrompt();
